import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Search, Building2, MapPin, Phone, Plus, Trash2, X, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  'Pending': { bg: 'bg-[#FFF8E7]', text: 'text-[#92600a]', border: 'border-[#D4AF37]/40' },
  'Active': { bg: 'bg-[#F0FFF4]', text: 'text-[#15803D]', border: 'border-green-200' },
  'Suspended': { bg: 'bg-[#FEF2F2]', text: 'text-[#D71920]', border: 'border-red-200' },
  'Rejected': { bg: 'bg-[#F7F7F7]', text: 'text-[#777]', border: 'border-[#E5E5E5]' },
  'Archived': { bg: 'bg-[#F7F7F7]', text: 'text-[#555]', border: 'border-[#E5E5E5]' },
};

const SCHOOL_STATUSES = ['Pending', 'Active', 'Suspended', 'Rejected', 'Archived'];

const Schools = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [showDeleted, setShowDeleted] = useState(false);

  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status_filter', statusFilter);
      if (showDeleted) params.append('show_deleted', 'true');
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      const response = await axios.get(`${API_URL}/schools?${params.toString()}`);
      setSchools(response.data);
    } catch (error) {
      console.error('Failed to fetch schools:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showDeleted, searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => { fetchSchools(); }, 300);
    return () => clearTimeout(t);
  }, [fetchSchools]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    status ? setSearchParams({ status }) : setSearchParams({});
  };

  const resetFilters = () => { setSearchTerm(''); handleStatusFilter(''); setShowDeleted(false); };
  const hasActiveFilters = searchTerm || statusFilter || showDeleted;

  return (
    <AdminLayout>
      <div className="space-y-5 max-w-6xl w-full">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs font-bold text-[#D71920] uppercase tracking-widest mb-1">Pentadbiran</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid="page-title">Senarai Perguruan</h1>
            <p className="text-sm text-[#777] mt-0.5">Urus semua perguruan silat di Sarawak</p>
          </div>
          <button
            onClick={() => navigate('/admin/schools/new')}
            className="flex items-center gap-2 bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-semibold px-4 py-2 rounded transition-colors self-start sm:self-auto"
            data-testid="add-school-button"
          >
            <Plus size={14} /> Daftar Perguruan
          </button>
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        {/* Filters */}
        <div className="bg-white rounded border border-[#E5E5E5] p-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Cari nama, ketua jurulatih, email atau telefon..."
                className="pl-9 border-[#E5E5E5] text-sm"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {['', ...SCHOOL_STATUSES].map((s) => (
              <button
                key={s || 'all'}
                onClick={() => handleStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                  statusFilter === s
                    ? 'bg-[#0B0B0B] text-white border-[#0B0B0B]'
                    : 'bg-white text-[#555] border-[#E5E5E5] hover:border-[#0B0B0B]'
                }`}
                data-testid={`filter-${s || 'all'}`}
              >
                {s || 'Semua'}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[#F7F7F7]">
            <div className="flex items-center gap-2">
              <Switch id="show-deleted" checked={showDeleted} onCheckedChange={setShowDeleted} data-testid="show-deleted-toggle" />
              <Label htmlFor="show-deleted" className="text-xs text-[#555] cursor-pointer">Tunjuk yang dipadam</Label>
            </div>
            {hasActiveFilters && (
              <button onClick={resetFilters} className="flex items-center gap-1 text-xs text-[#D71920] font-semibold hover:underline" data-testid="reset-filters-button">
                <X size={12} /> Reset Penapis
              </button>
            )}
          </div>
        </div>

        {/* Schools Grid */}
        {loading ? (
          <div className="text-center py-16 text-sm text-[#999] animate-pulse">Memuatkan perguruan...</div>
        ) : schools.length === 0 ? (
          <div className="text-center py-16 bg-white rounded border border-[#E5E5E5]">
            <Building2 size={36} className="text-[#E5E5E5] mx-auto mb-3" />
            <p className="text-sm text-[#777]">Tiada perguruan dijumpai</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {schools.map((school) => {
              const sc = statusConfig[school.status] || statusConfig['Rejected'];
              return (
                <div
                  key={school.id}
                  onClick={() => navigate(`/admin/schools/${school.id}`)}
                  className={`bg-white rounded border cursor-pointer transition-all hover:shadow-md hover:border-[#D71920]/30 overflow-hidden ${
                    school.is_deleted ? 'opacity-50 border-[#E5E5E5]' : 'border-[#E5E5E5]'
                  }`}
                  data-testid={`school-card-${school.id}`}
                >
                  <div className="p-4">
                    {/* Top row: icon + name + status */}
                    <div className="flex items-start gap-3 mb-3">
                      {school.logo_url ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${school.logo_url}`}
                          alt={school.name}
                          className="w-9 h-9 rounded object-cover flex-shrink-0 mt-0.5"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Building2 size={16} className="text-[#D71920]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-[#0B0B0B] text-sm leading-snug line-clamp-2 mb-1.5">{school.name}</h3>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${sc.bg} ${sc.text} ${sc.border}`}>
                            {school.status}
                          </span>
                          {school.is_deleted && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold border bg-black/10 text-black/60 border-black/20">
                              Dipadam
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Details */}
                    <div className="space-y-1 text-xs text-[#777]">
                      <div className="flex items-center gap-1.5">
                        <MapPin size={11} className="text-[#D71920] flex-shrink-0" />
                        <span className="truncate">{school.division}, {school.district}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Phone size={11} className="text-[#D71920] flex-shrink-0" />
                        <span>{school.phone}</span>
                      </div>
                    </div>
                    <div className="flex justify-end mt-3 pt-2 border-t border-[#F7F7F7]">
                      <span className="text-[10px] font-semibold text-[#D71920] flex items-center gap-0.5">
                        Lihat Butiran <ChevronRight size={10} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center text-xs text-[#999]" data-testid="schools-count">
          {schools.length} perguruan dijumpai
        </div>
      </div>
    </AdminLayout>
  );
};

export default Schools;
